import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'

/**
 * Универсальный загрузчик документа в приватный Supabase Storage bucket.
 * Используется на формах справочников «Пользователи» и «Компании»:
 *   • паспорта инспектора и доверенность менеджера (`user-documents`);
 *   • факсимиле и печать компании (`company-assets`).
 *
 * Сами bucket'ы и RLS-политики заведены миграцией `20260505130000` репо `stryzh`.
 *
 * Контракт:
 *   • `currentPath` — текущий путь объекта в bucket'е (хранится в БД); опциональный,
 *     если документ ещё не загружен.
 *   • `getSignedUrl(path)` — асинхронно вернуть signed URL для предпросмотра/скачивания
 *     (bucket приватный, нельзя `getPublicUrl`).
 *   • `onUpload(file)` — загрузить файл и вернуть путь в bucket'е (см. `uploadUserDocument`
 *     / `uploadCompanyAsset`).
 *   • `onPathChange(path)` — сообщить новой строке путь (родитель пишет в `draft`).
 *   • `accept` — `accept` для `<input type="file">` (по умолчанию `image/*,application/pdf`).
 */
export interface DocumentUploaderProps {
  label: string
  currentPath: string | null
  editing: boolean
  accept?: string
  getSignedUrl: (path: string) => Promise<string | null>
  onUpload: (file: File) => Promise<string>
  onPathChange: (path: string | null) => void
}

export function DocumentUploader({
  label,
  currentPath,
  editing,
  accept = 'image/*,application/pdf',
  getSignedUrl,
  onUpload,
  onPathChange,
}: DocumentUploaderProps) {
  const { t } = useTranslation()
  const [signedUrl, setSignedUrl] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    if (!currentPath) {
      setSignedUrl(null)
      return
    }
    void getSignedUrl(currentPath).then((url) => {
      if (!cancelled) setSignedUrl(url)
    })
    return () => {
      cancelled = true
    }
  }, [currentPath, getSignedUrl])

  async function handleFile(file: File | null) {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const path = await onUpload(file)
      onPathChange(path)
    } catch (e) {
      setError(e instanceof Error ? e.message : t('docUploader.uploadFailed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="stryzh-input-label">{label}</span>
      <div className="flex flex-wrap items-center gap-2">
        {signedUrl ? (
          <a
            href={signedUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline"
          >
            {t('docUploader.open')}
          </a>
        ) : (
          <span className="text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
            {currentPath ? t('docUploader.uploadedNoAccess') : t('docUploader.notUploaded')}
          </span>
        )}
        {editing ? (
          <>
            <label className="cursor-pointer text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-accent-orange)] hover:underline">
              {currentPath ? t('docUploader.replace') : t('docUploader.upload')}
              <input
                type="file"
                accept={accept}
                disabled={busy}
                onChange={(e) => {
                  void handleFile(e.target.files?.[0] ?? null)
                  e.target.value = ''
                }}
                className="hidden"
              />
            </label>
            {currentPath ? (
              <Button
                variant="outline"
                onClick={() => onPathChange(null)}
              >
                {t('docUploader.removeLink')}
              </Button>
            ) : null}
          </>
        ) : null}
      </div>
      {busy ? (
        <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('common.loading')}
        </span>
      ) : null}
      {error ? (
        <span className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-accent-red)]">
          {error}
        </span>
      ) : null}
    </div>
  )
}
